// FabsTransactionsStatement.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit, faTrash, faEye, faSave, faSearch, faRefresh, faSpinner,
    faExclamationTriangle, faCheckCircle, faTimes, faChevronLeft, faChevronRight,
    faFilter, faCalendarAlt, faFilePdf, faPrint, faDownload, faCalendar,
    faTimesCircle, faBox, faTag, faBuilding, faBalanceScale, faDollarSign, faUndoAlt,
    faCalculator, faExclamationCircle, faHashtag, faPlus, faBarcode, faArrowRight,
    faLayerGroup, faIndustry, faWeightHanging, faWarehouse, faFileExport,
    faChartLine, faPercent, faUser, faPalette, faRulerCombined, faBoxes as faBoxesIcon,
    faInfoCircle, faHistory, faStore, faExchangeAlt, faShoppingCart, faTruck,
    faShip, faRoad, faMoneyBillWave, faUniversity, faMoneyCheck, faCreditCard,
    faPhone, faEnvelope, faMapMarkerAlt, faGlobe, faClock, faCalendarCheck,
    faFileInvoice, faReceipt, faMoneyBill, faDownload as faDownloadIcon, faMobile,
    faHourglassHalf, faCheckDouble, faMapMarkedAlt, faFlag, faClock as faClockIcon,
    faIdCard, faAddressCard, faComment, faMoneyCheckAlt, faBan, faBell, faMinus,
    faArrowsAlt, faExpand, faCompress, faMoneyBillTransfer, faUsers, faUserFriends,
    faHandshake, faUserTie, faUsersCog, faChartPie, faChartBar, faChevronDown,
    faChevronUp, faSort, faSortDown, faSortUp, faArrowUp, faArrowDown,
    faWallet, faPiggyBank, faHandHoldingUsd, faLandmark, faCoins, faStickyNote
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import "../styles/CustomerList.css";
import "../styles/Customer.css";

// ==============================================
// FABS UNIFIED TRANSACTIONS STATEMENT COMPONENT
// Complete Unified Transaction History with Advanced Filters
// ==============================================

// Translator instance
const createFabsTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_fabs_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => cache.set(key, value));
            }
        } catch (e) { console.log("No cache found"); }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => { cacheObj[key] = value; });
            localStorage.setItem('ourzest_fabs_translations', JSON.stringify(cacheObj));
        } catch (e) { console.error("Error saving to cache:", e); }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;
        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) { console.log("Translation failed"); }
        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) return cache.get(cacheKey);
        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();
        return translated;
    };

    const defaultTexts = {
        title: 'Transactions Statement',
        subtitle: 'Complete Transaction History with Advanced Analytics',
        transactions: 'Transactions',
        shopName: 'Shop',
        noShopSelected: 'No shop selected. Please select a shop first.',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        searchPlaceholder: 'Search by name, receipt, reference, description...',
        refresh: 'Refresh',
        loadingData: 'Loading transaction data...',
        noTransactionsFound: 'No transactions found',
        
        // Entity Types
        allEntities: 'All Entities',
        customers: 'Customers',
        suppliers: 'Suppliers',
        partners: 'Partners',
        employees: 'Employees',
        others: 'Others',
        owner: 'Owner',
        
        // Transaction Types
        allTypes: 'All Types',
        customerPayment: 'Customer Payment',
        customerSale: 'Customer Sale',
        customerRefund: 'Customer Return/Refund',
        customerTransfer: 'Customer Transfer',
        supplierPayment: 'Supplier Payment',
        supplierReturn: 'Supplier Return',
        salaryPayment: 'Salary Payment',
        salaryAdvance: 'Salary Advance',
        generalExpense: 'General Expense',
        otherPurchase: 'Other Purchase',
        otherPayment: 'Other Payment',
        partnerToOwner: 'Partner → Owner',
        partnerToPartner: 'Partner → Partner',
        partnerToSupplier: 'Partner → Supplier',
        partnerSalary: 'Partner Paid Salary',
        partnerToOther: 'Partner → Other',
        ownerToOther: 'Owner → Other',
        otherToOwner: 'Other → Owner',
        ownerCommission: 'Owner Commission',
        ownerTransferIn: 'Owner Transfer In',
        ownerTransferOut: 'Owner Transfer Out',
        ownerExpense: 'Owner Expense',
        currencyExchange: 'Currency Exchange',
        
        // Payment Methods
        paymentMethod: 'Payment Method',
        allMethods: 'All Methods',
        cash: 'Cash',
        bankTransfer: 'Bank Transfer',
        digitalWallet: 'Digital Wallet',
        card: 'Card',
        upi: 'UPI',
        cheque: 'Cheque',
        
        // Transaction Direction
        direction: 'Direction',
        allDirections: 'All Directions',
        incoming: 'Incoming',
        outgoing: 'Outgoing',
        
        // Expense Categories
        expenseCategory: 'Expense Category',
        allCategories: 'All Categories',
        rent: 'Rent',
        utilities: 'Utilities',
        payroll: 'Payroll',
        marketing: 'Marketing',
        maintenance: 'Maintenance',
        travel: 'Travel',
        supplies: 'Supplies',
        insurance: 'Insurance',
        taxes: 'Taxes',
        other: 'Other',
        
        // Amount & Date
        amountRange: 'Amount Range',
        minAmount: 'Min Amount',
        maxAmount: 'Max Amount',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        currencyFilter: 'Currency',
        allCurrencies: 'All Currencies',
        
        // Table Headers
        serialNo: 'S.No',
        transactionDate: 'Date',
        transactionType: 'Type',
        entityType: 'Entity Type',
        entityName: 'Entity Name',
        description: 'Description',
        amount: 'Amount',
        currency: 'Currency',
        paymentMethod: 'Payment Method',
        referenceNumber: 'Reference No',
        paymentSource: 'Payment Source',
        status: 'Status',
        actions: 'Actions',
        
        // Summary Stats
        totalTransactions: 'Total Transactions',
        totalIncoming: 'Total Incoming',
        totalOutgoing: 'Total Outgoing',
        netBalance: 'Net Balance',
        averageTransaction: 'Avg Transaction',
        largestTransaction: 'Largest Transaction',
        currencyTotals: 'Currency-wise Summary',
        currencyBreakdown: 'Currency-wise Transaction Breakdown',
        
        // Filter Groups
        entityFilter: 'Entity Type',
        typeFilter: 'Transaction Type',
        methodFilter: 'Payment Method',
        sourceFilter: 'Payment Source',
        categoryFilter: 'Expense Category',
        
        // Buttons
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        exportToPDF: 'Export PDF',
        exportToCSV: 'Export CSV',
        exportToExcel: 'Export Excel',
        print: 'Print',
        viewDetails: 'View Details',
        close: 'Close',
        
        // Modal
        transactionDetails: 'Transaction Details',
        balanceInfo: 'Balance Information',
        beforeBalance: 'Balance Before',
        afterBalance: 'Balance After',
        amountInBaseCurrency: 'Amount (Base Currency)',
        exchangeRate: 'Exchange Rate',
        
        // Pagination
        showing: 'Showing',
        of: 'of',
        previous: 'Previous',
        next: 'Next',
        
        // Toast Messages
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        info: 'Info',
        
        // Charts
        monthlyBreakdown: 'Monthly Breakdown',
        topCustomers: 'Top Customers',
        expenseByCategory: 'Expenses by Category',
        cashFlow: 'Cash Flow',
        
        // Additional
        generatedBy: 'Generated by',
        reportDate: 'Report Date',
        totalCount: 'Total Count',
        viewStatement: 'View Statement'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') return defaultTexts;
        const translations = {};
        const promises = Object.keys(defaultTexts).map(key =>
            translateText(defaultTexts[key], lang).then(translated => { translations[key] = translated; })
        );
        await Promise.all(promises);
        saveToCache();
        return translations;
    };

    loadFromCache();
    return { getTranslations, translateText };
};

const fabsTranslator = createFabsTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations.success || 'Success';
            case 'error': return translations.error || 'Error';
            case 'warning': return translations.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content shop-selector-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Type Badge Component
const TypeBadge = ({ type, translations, size = 'sm' }) => {
    const getTypeStyle = (type) => {
        const styles = {
            customer_payment: { bg: '#e0f2fe', color: '#0369a1', border: '#0369a140', icon: faMoneyBillWave, label: 'Customer Payment' },
            customer_sale: { bg: '#dcfce7', color: '#166534', border: '#16653440', icon: faShoppingCart, label: 'Customer Sale' },
            customer_refund: { bg: '#fef9c3', color: '#854d0e', border: '#854d0e40', icon: faUndoAlt, label: 'Customer Refund' },
            customer_transfer: { bg: '#fce7f3', color: '#9d174d', border: '#9d174d40', icon: faExchangeAlt, label: 'Customer Transfer' },
            supplier_payment: { bg: '#fee2e2', color: '#991b1b', border: '#991b1b40', icon: faTruck, label: 'Supplier Payment' },
            supplier_return: { bg: '#d1fae5', color: '#065f46', border: '#065f4640', icon: faTruck, label: 'Supplier Return' },
            salary_payment: { bg: '#fef3c7', color: '#b45309', border: '#b4530940', icon: faUser, label: 'Salary Payment' },
            salary_advance: { bg: '#ffedd5', color: '#9a3412', border: '#9a341240', icon: faClock, label: 'Salary Advance' },
            general_expense: { bg: '#f1f5f9', color: '#475569', border: '#47556940', icon: faCalculator, label: 'General Expense' },
            other_payment: { bg: '#e0e7ff', color: '#3730a3', border: '#3730a340', icon: faMoneyBill, label: 'Other Payment' },
            other_purchase: { bg: '#e5e7eb', color: '#374151', border: '#37415140', icon: faBox, label: 'Other Purchase' },
            partner_to_owner: { bg: '#fef3c7', color: '#92400e', border: '#92400e40', icon: faHandshake, label: 'Partner → Owner' },
            partner_to_partner: { bg: '#c7d2fe', color: '#3730a3', border: '#3730a340', icon: faUsers, label: 'Partner → Partner' },
            partner_to_supplier: { bg: '#bfdbfe', color: '#1e40af', border: '#1e40af40', icon: faHandshake, label: 'Partner → Supplier' },
            partner_salary: { bg: '#ddd6fe', color: '#6d28d9', border: '#6d28d940', icon: faUser, label: 'Partner Paid Salary' },
            partner_to_other: { bg: '#c4b5fd', color: '#5b21b6', border: '#5b21b640', icon: faUserFriends, label: 'Partner → Other' },
            owner_to_other: { bg: '#fbcfe8', color: '#be185d', border: '#be185d40', icon: faArrowRight, label: 'Owner → Other' },
            other_to_owner: { bg: '#ede9fe', color: '#5b21b6', border: '#5b21b640', icon: faUserTie, label: 'Other → Owner' },
            owner_commission: { bg: '#ccfbf1', color: '#0f766e', border: '#0f766e40', icon: faPercent, label: 'Owner Commission' },
            owner_transfer_in: { bg: '#a7f3d0', color: '#065f46', border: '#065f4640', icon: faArrowDown, label: 'Owner Transfer In' },
            owner_transfer_out: { bg: '#fed7aa', color: '#c2410c', border: '#c2410c40', icon: faArrowUp, label: 'Owner Transfer Out' },
            owner_expense: { bg: '#fecaca', color: '#dc2626', border: '#dc262640', icon: faUserTie, label: 'Owner Expense' },
            currency_exchange: { bg: '#e9d5ff', color: '#7e22ce', border: '#7e22ce40', icon: faExchangeAlt, label: 'Currency Exchange' }
        };
        return styles[type] || { bg: '#6b728020', color: '#6b7280', border: '#6b728040', icon: faMoneyBill, label: type };
    };

    const style = getTypeStyle(type);
    const label = translations[type] || style.label;
    const padding = size === 'sm' ? '4px 8px' : '6px 12px';
    const fontSize = size === 'sm' ? '12px' : '13px';

    return (
        <div className="type-badge" style={{
            backgroundColor: style.bg,
            color: style.color,
            border: `1px solid ${style.border}`,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            padding: padding,
            borderRadius: '20px',
            fontSize: fontSize
        }}>
            <FontAwesomeIcon icon={style.icon} size="xs" />
            {label}
        </div>
    );
};

// Entity Badge Component
const EntityBadge = ({ entityType, translations }) => {
    const getEntityStyle = (type) => {
        const styles = {
            customer: { bg: '#e0f2fe', color: '#0369a1', icon: faUser },
            supplier: { bg: '#fee2e2', color: '#991b1b', icon: faTruck },
            partner: { bg: '#fef3c7', color: '#92400e', icon: faHandshake },
            employee: { bg: '#dcfce7', color: '#166534', icon: faUserFriends },
            other: { bg: '#f1f5f9', color: '#475569', icon: faBuilding },
            owner: { bg: '#ede9fe', color: '#5b21b6', icon: faUserTie }
        };
        return styles[type] || { bg: '#6b728020', color: '#6b7280', icon: faBuilding };
    };

    const style = getEntityStyle(entityType);
    const label = translations[entityType] || entityType;

    return (
        <div className="entity-badge" style={{
            backgroundColor: style.bg,
            color: style.color,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            padding: '2px 6px',
            borderRadius: '12px',
            fontSize: '11px'
        }}>
            <FontAwesomeIcon icon={style.icon} size="xs" />
            {label}
        </div>
    );
};

// Payment Method Badge
const PaymentMethodBadge = ({ method, translations }) => {
    const getMethodStyle = (method) => {
        const styles = {
            cash: { bg: '#dcfce7', color: '#166534', icon: faMoneyBillWave },
            bank_transfer: { bg: '#e0e7ff', color: '#3730a3', icon: faUniversity },
            digital_wallet: { bg: '#fef3c7', color: '#92400e', icon: faMobile },
            card: { bg: '#e0f2fe', color: '#0369a1', icon: faCreditCard },
            upi: { bg: '#fce7f3', color: '#9d174d', icon: faMobile },
            cheque: { bg: '#f1f5f9', color: '#475569', icon: faMoneyCheck }
        };
        return styles[method] || { bg: '#6b728020', color: '#6b7280', icon: faMoneyBill };
    };

    const style = getMethodStyle(method);
    const label = translations[method] || method;

    return (
        <div className="payment-badge" style={{
            backgroundColor: style.bg,
            color: style.color,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            padding: '2px 6px',
            borderRadius: '12px',
            fontSize: '11px'
        }}>
            <FontAwesomeIcon icon={style.icon} size="xs" />
            {label}
        </div>
    );
};

// Currency Symbols
const getCurrencySymbol = (currency) => {
    const symbols = { 
        USD: '$', 
        EUR: '€', 
        GBP: '£', 
        PKR: 'Rs', 
        USDT: '₮', 
        INR: '₹',
        AED: 'د.إ',
        SAR: '﷼',
        CNY: '¥',
        JPY: '¥',
        CAD: 'C$',
        AUD: 'A$'
    };
    return symbols[currency] || currency;
};

// Format currency function with proper symbol
const formatCurrencyWithSymbol = (amount, currency = 'USD') => {
    if (!amount && amount !== 0) return `${getCurrencySymbol(currency)} 0.00`;
    const symbol = getCurrencySymbol(currency);
    return `${symbol} ${parseFloat(amount).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;
};

// Date formatter
const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString();
    } catch { return dateString; }
};

// Currency Breakdown Modal Component
const CurrencyBreakdownModal = ({ isOpen, onClose, transactions, translations }) => {
    if (!isOpen) return null;
    
    const isIncomingTransactionType = (type) => {
        const incomingTypes = [
            'customer_payment', 'customer_sale', 'customer_refund', 'supplier_return',
            'other_payment', 'partner_to_owner', 'other_to_owner', 'owner_commission',
            'owner_transfer_in'
        ];
        return incomingTypes.includes(type);
    };
    
    const getTransactionsByCurrency = () => {
        const byCurrency = {};
        transactions.forEach(t => {
            const currency = t.currency_code || 'USD';
            if (!byCurrency[currency]) {
                byCurrency[currency] = [];
            }
            byCurrency[currency].push(t);
        });
        return byCurrency;
    };
    
    const byCurrency = getTransactionsByCurrency();
    
    return (
        <div className="modal-overlay-modern" onClick={onClose}>
            <div className="modal-content-modern" style={{ maxWidth: '90%', width: '1200px' }} onClick={e => e.stopPropagation()}>
                <div className="modal-header-modern">
                    <div className="modal-icon">
                        <FontAwesomeIcon icon={faCoins} />
                    </div>
                    <div className="modal-title">
                        <h3>{translations.currencyBreakdown || 'Currency-wise Transaction Breakdown'}</h3>
                    </div>
                    <button className="close-btn-modern" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body-modern" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {Object.entries(byCurrency).map(([currency, currencyTransactions]) => {
                        const totalIncoming = currencyTransactions
                            .filter(t => isIncomingTransactionType(t.transaction_type))
                            .reduce((sum, t) => sum + parseFloat(t.amount), 0);
                        const totalOutgoing = currencyTransactions
                            .filter(t => !isIncomingTransactionType(t.transaction_type))
                            .reduce((sum, t) => sum + parseFloat(t.amount), 0);
                        
                        return (
                            <div key={currency} style={{ marginBottom: '30px' }}>
                                <div style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    padding: '10px 15px',
                                    borderRadius: '8px',
                                    color: 'white',
                                    marginBottom: '15px'
                                }}>
                                    <h4 style={{ margin: 0 }}>
                                        {getCurrencySymbol(currency)} {currency}
                                        <span style={{ fontSize: '12px', marginLeft: '10px', opacity: 0.8 }}>
                                            ({currencyTransactions.length} transactions)
                                        </span>
                                    </h4>
                                    <div style={{ fontSize: '12px', marginTop: '5px' }}>
                                        In: {formatCurrencyWithSymbol(totalIncoming, currency)} | 
                                        Out: {formatCurrencyWithSymbol(totalOutgoing, currency)} | 
                                        Net: {formatCurrencyWithSymbol(totalIncoming - totalOutgoing, currency)}
                                    </div>
                                </div>
                                
                                <div className="table-scroll-container">
                                    <table className="customer-table-modern">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Type</th>
                                                <th>Entity</th>
                                                <th>Name</th>
                                                <th>Description</th>
                                                <th>Amount</th>
                                                <th>Payment Method</th>
                                                <th>Reference</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currencyTransactions.map(tx => (
                                                <tr key={tx.id}>
                                                    <td>{formatDate(tx.transaction_date)}</td>
                                                    <td><TypeBadge type={tx.transaction_type} translations={translations} size="sm" /></td>
                                                    <td><EntityBadge entityType={tx.entity_type} translations={translations} /></td>
                                                    <td>{tx.entity_name || '-'}</td>
                                                    <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {tx.description || '-'}
                                                    </td>
                                                    <td style={{ 
                                                        color: isIncomingTransactionType(tx.transaction_type) ? '#10b981' : '#ef4444',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {isIncomingTransactionType(tx.transaction_type) ? '+' : '-'}
                                                        {formatCurrencyWithSymbol(parseFloat(tx.amount), currency)}
                                                    </td>
                                                    <td>{tx.payment_method || '-'}</td>
                                                    <td>{tx.reference_number || tx.transaction_no || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="modal-footer-modern">
                    <button className="btn-modern btn-secondary-modern" onClick={onClose}>
                        {translations.close || 'Close'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Main Component
const FabsTransactionsStatement = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();

    // State
    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);
    const [summary, setSummary] = useState(null);
    const [currencyTotals, setCurrencyTotals] = useState({});
    const [showCurrencyBreakdown, setShowCurrencyBreakdown] = useState(false);

    // Available options for filters
    const [customers, setCustomers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [partners, setPartners] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [others, setOthers] = useState([]);
    const [currencies, setCurrencies] = useState(['USD', 'PKR', 'USDT', 'EUR', 'GBP']);

    // Filter states
    const [filterEntityType, setFilterEntityType] = useState('');
    const [filterEntityId, setFilterEntityId] = useState('');
    const [filterTransactionType, setFilterTransactionType] = useState('');
    const [filterPaymentMethod, setFilterPaymentMethod] = useState('');
    const [filterPaymentSource, setFilterPaymentSource] = useState('');
    const [filterExpenseCategory, setFilterExpenseCategory] = useState('');
    const [filterCurrency, setFilterCurrency] = useState('');
    const [filterDirection, setFilterDirection] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [filterAmountMin, setFilterAmountMin] = useState('');
    const [filterAmountMax, setFilterAmountMax] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Sorting
    const [sortField, setSortField] = useState('transaction_date');
    const [sortDirection, setSortDirection] = useState('desc');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);

    // UI State
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });
    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [currentTheme, setCurrentTheme] = useState(() => localStorage.getItem('ourzest_theme') || '#8b5cf6');
    const [translations, setTranslations] = useState({});
    const [pdfLoading, setPdfLoading] = useState(false);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(true);

    const toastTimerRef = useRef(null);

    // Transaction type options
    const transactionTypes = [
        { value: 'customer_payment', label: 'Customer Payment', category: 'incoming', entity: 'customer' },
        { value: 'customer_sale', label: 'Customer Sale', category: 'incoming', entity: 'customer' },
        { value: 'customer_refund', label: 'Customer Refund', category: 'incoming', entity: 'customer' },
        { value: 'customer_transfer', label: 'Customer Transfer', category: 'both', entity: 'customer' },
        { value: 'supplier_payment', label: 'Supplier Payment', category: 'outgoing', entity: 'supplier' },
        { value: 'supplier_return', label: 'Supplier Return', category: 'incoming', entity: 'supplier' },
        { value: 'salary_payment', label: 'Salary Payment', category: 'outgoing', entity: 'employee' },
        { value: 'salary_advance', label: 'Salary Advance', category: 'outgoing', entity: 'employee' },
        { value: 'general_expense', label: 'General Expense', category: 'outgoing', entity: 'owner' },
        { value: 'other_payment', label: 'Other Payment', category: 'incoming', entity: 'other' },
        { value: 'other_purchase', label: 'Other Purchase', category: 'outgoing', entity: 'other' },
        { value: 'partner_to_owner', label: 'Partner → Owner', category: 'incoming', entity: 'partner' },
        { value: 'partner_to_partner', label: 'Partner → Partner', category: 'both', entity: 'partner' },
        { value: 'partner_to_supplier', label: 'Partner → Supplier', category: 'outgoing', entity: 'partner' },
        { value: 'partner_salary', label: 'Partner Paid Salary', category: 'outgoing', entity: 'partner' },
        { value: 'partner_to_other', label: 'Partner → Other', category: 'outgoing', entity: 'partner' },
        { value: 'owner_to_other', label: 'Owner → Other', category: 'outgoing', entity: 'owner' },
        { value: 'other_to_owner', label: 'Other → Owner', category: 'incoming', entity: 'other' },
        { value: 'owner_commission', label: 'Owner Commission', category: 'incoming', entity: 'owner' },
        { value: 'owner_transfer_in', label: 'Owner Transfer In', category: 'incoming', entity: 'owner' },
        { value: 'owner_transfer_out', label: 'Owner Transfer Out', category: 'outgoing', entity: 'owner' },
        { value: 'owner_expense', label: 'Owner Expense', category: 'outgoing', entity: 'owner' },
        { value: 'currency_exchange', label: 'Currency Exchange', category: 'both', entity: 'owner' }
    ];

    const entityTypes = [
        { value: 'customer', label: 'Customers', icon: faUser },
        { value: 'supplier', label: 'Suppliers', icon: faTruck },
        { value: 'partner', label: 'Partners', icon: faHandshake },
        { value: 'employee', label: 'Employees', icon: faUserFriends },
        { value: 'other', label: 'Others', icon: faBuilding },
        { value: 'owner', label: 'Owner', icon: faUserTie }
    ];

    const paymentMethods = [
        { value: 'cash', label: 'Cash', icon: faMoneyBillWave },
        { value: 'bank_transfer', label: 'Bank Transfer', icon: faUniversity },
        { value: 'digital_wallet', label: 'Digital Wallet', icon: faMobile },
        { value: 'card', label: 'Card', icon: faCreditCard },
        { value: 'upi', label: 'UPI', icon: faMobile },
        { value: 'cheque', label: 'Cheque', icon: faMoneyCheck }
    ];

    const expenseCategoryOptions = [
        { value: 'rent', label: 'Rent' },
        { value: 'utilities', label: 'Utilities' },
        { value: 'payroll', label: 'Payroll' },
        { value: 'marketing', label: 'Marketing' },
        { value: 'maintenance', label: 'Maintenance' },
        { value: 'travel', label: 'Travel' },
        { value: 'supplies', label: 'Supplies' },
        { value: 'insurance', label: 'Insurance' },
        { value: 'taxes', label: 'Taxes' },
        { value: 'other', label: 'Other' }
    ];

    // Helper functions
    const isIncomingTransaction = (type) => {
        const incomingTypes = [
            'customer_payment', 'customer_sale', 'customer_refund', 'supplier_return',
            'other_payment', 'partner_to_owner', 'other_to_owner', 'owner_commission',
            'owner_transfer_in'
        ];
        return incomingTypes.includes(type);
    };

    const isOutgoingTransaction = (type) => {
        const outgoingTypes = [
            'supplier_payment', 'salary_payment', 'salary_advance', 'general_expense',
            'other_purchase', 'owner_expense', 'owner_transfer_out', 'owner_to_other',
            'partner_to_supplier', 'partner_salary', 'partner_to_other'
        ];
        return outgoingTypes.includes(type);
    };

    // Calculate currency totals
    const calculateCurrencyTotals = useCallback((transactionsList) => {
        const totals = {};
        transactionsList.forEach(t => {
            const currency = t.currency_code || 'USD';
            const amount = parseFloat(t.amount) || 0;
            const isIncoming = isIncomingTransaction(t.transaction_type);
            
            if (!totals[currency]) {
                totals[currency] = {
                    incoming: 0,
                    outgoing: 0,
                    net: 0,
                    count: 0
                };
            }
            
            if (isIncoming) {
                totals[currency].incoming += amount;
                totals[currency].net += amount;
            } else if (isOutgoingTransaction(t.transaction_type)) {
                totals[currency].outgoing += amount;
                totals[currency].net -= amount;
            }
            totals[currency].count++;
        });
        setCurrencyTotals(totals);
    }, []);

    // Calculate summary
    const calculateSummary = (data) => {
        const totalIncoming = data
            .filter(t => isIncomingTransaction(t.transaction_type))
            .reduce((sum, t) => sum + parseFloat(t.amount), 0);
        
        const totalOutgoing = data
            .filter(t => isOutgoingTransaction(t.transaction_type))
            .reduce((sum, t) => sum + parseFloat(t.amount), 0);
        
        const amounts = data.map(t => parseFloat(t.amount));
        const maxAmount = amounts.length > 0 ? Math.max(...amounts) : 0;
        const avgAmount = amounts.length > 0 ? amounts.reduce((a, b) => a + b, 0) / amounts.length : 0;
        
        setSummary({
            totalCount: data.length,
            totalIncoming,
            totalOutgoing,
            netBalance: totalIncoming - totalOutgoing,
            averageTransaction: avgAmount,
            largestTransaction: maxAmount
        });
        
        calculateCurrencyTotals(data);
    };

    // Calculate page currency totals
    const calculatePageCurrencyTotals = (transactionsList) => {
        const totals = {};
        transactionsList.forEach(t => {
            const currency = t.currency_code || 'USD';
            const amount = parseFloat(t.amount) || 0;
            const isIncoming = isIncomingTransaction(t.transaction_type);
            
            if (!totals[currency]) {
                totals[currency] = { incoming: 0, outgoing: 0 };
            }
            
            if (isIncoming) {
                totals[currency].incoming += amount;
            } else if (isOutgoingTransaction(t.transaction_type)) {
                totals[currency].outgoing += amount;
            }
        });
        return totals;
    };

    // Show toast
    const showToast = (type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    };

    // Clean up
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';
                document.documentElement.style.setProperty('--primary-color', color);
                document.documentElement.style.setProperty('--primary-gradient', `linear-gradient(135deg, ${color}, #7c3aed)`);
            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };
        applyTheme(currentTheme);
    }, [currentTheme]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await fabsTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        if (isRTL) {
            document.documentElement.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
        }
    }, [currentLanguage]);

    // Fetch data
    useEffect(() => {
        if (user && user.username && currentShop) {
            fetchTransactions();
            fetchEntityOptions();
        } else if (user && user.username) {
            fetchAvailableShops();
            setLoading(false);
        } else {
            setLoading(false);
            showToast('error', 'User not authenticated');
        }
    }, [user, currentShop]);

    // Apply filters
    useEffect(() => {
        applyFilters();
    }, [transactions, filterEntityType, filterEntityId, filterTransactionType, filterPaymentMethod,
        filterPaymentSource, filterExpenseCategory, filterCurrency, filterDirection,
        filterDateFrom, filterDateTo, filterAmountMin, filterAmountMax, searchTerm,
        sortField, sortDirection]);

    const fetchAvailableShops = async () => {
        if (!user) return;
        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
        } finally {
            setLoadingShops(false);
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', 'Shop switched successfully');
                fetchTransactions();
                fetchEntityOptions();
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            showToast('error', 'Failed to switch shop');
        }
    };

    const fetchEntityOptions = async () => {
        if (!currentShop) return;

        // Fetch customers
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/unified_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_customers',
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setCustomers(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
        }

        // Fetch suppliers
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/unified_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_suppliers',
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setSuppliers(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching suppliers:', error);
        }

        // Fetch partners
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/unified_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_partners',
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setPartners(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching partners:', error);
        }

        // Fetch employees
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/unified_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_employees',
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setEmployees(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        }

        // Fetch others
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/unified_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_other_accounts',
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setOthers(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching others:', error);
        }
    };

    const fetchTransactions = async () => {
        if (!currentShop) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/unified_transactions.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_transactions',
                    shop_id: currentShop.shop_id,
                    limit: 10000
                })
            });
            const data = await response.json();
            
            if (data.status === 'success') {
                setTransactions(data.data || []);
                calculateSummary(data.data || []);
            } else {
                showToast('error', data.message || 'Failed to load transactions');
            }
        } catch (error) {
            console.error('Error fetching transactions:', error);
            showToast('error', 'Failed to load transactions');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...transactions];

        if (filterEntityType) {
            filtered = filtered.filter(t => t.entity_type === filterEntityType);
            if (filterEntityId && filterEntityType) {
                filtered = filtered.filter(t => t.entity_id == filterEntityId);
            }
        }

        if (filterTransactionType) {
            filtered = filtered.filter(t => t.transaction_type === filterTransactionType);
        }

        if (filterDirection === 'incoming') {
            filtered = filtered.filter(t => isIncomingTransaction(t.transaction_type));
        } else if (filterDirection === 'outgoing') {
            filtered = filtered.filter(t => isOutgoingTransaction(t.transaction_type));
        }

        if (filterPaymentMethod) {
            filtered = filtered.filter(t => t.payment_method === filterPaymentMethod);
        }

        if (filterPaymentSource) {
            filtered = filtered.filter(t => t.payment_source === filterPaymentSource);
        }

        if (filterExpenseCategory) {
            filtered = filtered.filter(t => t.expense_category_name === filterExpenseCategory);
        }

        if (filterCurrency) {
            filtered = filtered.filter(t => t.currency_code === filterCurrency);
        }

        if (filterDateFrom) {
            filtered = filtered.filter(t => t.transaction_date >= filterDateFrom);
        }
        if (filterDateTo) {
            filtered = filtered.filter(t => t.transaction_date <= filterDateTo);
        }

        if (filterAmountMin) {
            filtered = filtered.filter(t => parseFloat(t.amount) >= parseFloat(filterAmountMin));
        }
        if (filterAmountMax) {
            filtered = filtered.filter(t => parseFloat(t.amount) <= parseFloat(filterAmountMax));
        }

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(t =>
                t.entity_name?.toLowerCase().includes(term) ||
                t.description?.toLowerCase().includes(term) ||
                t.reference_number?.toLowerCase().includes(term) ||
                t.transaction_no?.toLowerCase().includes(term) ||
                t.notes?.toLowerCase().includes(term)
            );
        }

        filtered.sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];
            
            if (sortField === 'amount') {
                aVal = parseFloat(aVal);
                bVal = parseFloat(bVal);
            } else if (sortField === 'transaction_date') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            }
            
            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        setFilteredTransactions(filtered);
        setCurrentPage(1);
        calculateCurrencyTotals(filtered);
    };

    const resetFilters = () => {
        setFilterEntityType('');
        setFilterEntityId('');
        setFilterTransactionType('');
        setFilterPaymentMethod('');
        setFilterPaymentSource('');
        setFilterExpenseCategory('');
        setFilterCurrency('');
        setFilterDirection('');
        setFilterDateFrom('');
        setFilterDateTo('');
        setFilterAmountMin('');
        setFilterAmountMax('');
        setSearchTerm('');
        setSortField('transaction_date');
        setSortDirection('desc');
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleViewTransaction = async (transaction) => {
        setSelectedTransaction(transaction);
        setShowViewModal(true);
    };

    const exportToCSV = () => {
        if (filteredTransactions.length === 0) {
            showToast('warning', 'No data to export');
            return;
        }

        const headers = [
            'Transaction No', 'Date', 'Type', 'Entity Type', 'Entity Name',
            'Description', 'Amount', 'Currency', 'Payment Method', 'Payment Source',
            'Reference No', 'Status'
        ];
        
        const rows = filteredTransactions.map(t => [
            t.transaction_no,
            formatDate(t.transaction_date),
            t.transaction_type,
            t.entity_type,
            t.entity_name,
            t.description || '',
            t.amount,
            t.currency_code,
            t.payment_method,
            t.payment_source,
            t.reference_number || '',
            t.status
        ]);

        const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showToast('success', 'CSV exported successfully');
    };

    const exportToPDF = async () => {
        if (filteredTransactions.length === 0) {
            showToast('warning', 'No data to export');
            return;
        }

        setPdfLoading(true);
        try {
            const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageWidth = pdf.internal.pageSize.getWidth();

            pdf.setFillColor(139, 92, 246);
            pdf.rect(0, 0, pageWidth, 40, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(18);
            pdf.text(translations.title || 'Unified Transactions Statement', 14, 20);
            pdf.setFontSize(10);
            pdf.text(`${translations.generatedBy || 'Generated by'}: ${user?.username || 'System'}`, 14, 32);
            pdf.text(`${translations.reportDate || 'Report Date'}: ${new Date().toLocaleDateString()}`, pageWidth - 50, 32);

            pdf.setTextColor(0, 0, 0);
            pdf.setFontSize(10);
            const totalIncoming = filteredTransactions.filter(t => isIncomingTransaction(t.transaction_type)).reduce((sum, t) => sum + parseFloat(t.amount), 0);
            const totalOutgoing = filteredTransactions.filter(t => isOutgoingTransaction(t.transaction_type)).reduce((sum, t) => sum + parseFloat(t.amount), 0);
            
            pdf.text(`${translations.totalTransactions || 'Total Transactions'}: ${filteredTransactions.length}`, 14, 55);
            pdf.text(`${translations.totalIncoming || 'Total Incoming'}: ${formatCurrencyWithSymbol(totalIncoming, 'USD')}`, 14, 62);
            pdf.text(`${translations.totalOutgoing || 'Total Outgoing'}: ${formatCurrencyWithSymbol(totalOutgoing, 'USD')}`, 80, 55);
            pdf.text(`${translations.netBalance || 'Net Balance'}: ${formatCurrencyWithSymbol(totalIncoming - totalOutgoing, 'USD')}`, 80, 62);

            const tableData = filteredTransactions.slice(0, 500).map(t => [
                t.transaction_no,
                formatDate(t.transaction_date),
                translations[t.transaction_type] || t.transaction_type,
                translations[t.entity_type] || t.entity_type,
                t.entity_name?.substring(0, 20) || '-',
                formatCurrencyWithSymbol(parseFloat(t.amount), t.currency_code),
                t.currency_code,
                translations[t.payment_method] || t.payment_method || '-'
            ]);

            autoTable(pdf, {
                startY: 70,
                head: [['Transaction No', 'Date', 'Type', 'Entity', 'Name', 'Amount', 'Currency', 'Payment Method']],
                body: tableData,
                theme: 'striped',
                headStyles: { fillColor: [139, 92, 246], textColor: [255, 255, 255] },
                styles: { fontSize: 7, cellPadding: 2 },
                columnStyles: {
                    0: { cellWidth: 25 },
                    1: { cellWidth: 20 },
                    2: { cellWidth: 25 },
                    3: { cellWidth: 20 },
                    4: { cellWidth: 35 },
                    5: { cellWidth: 25 },
                    6: { cellWidth: 15 },
                    7: { cellWidth: 20 }
                }
            });

            pdf.save(`transactions_${new Date().toISOString().split('T')[0]}.pdf`);
            showToast('success', 'PDF exported successfully');
        } catch (error) {
            console.error('PDF Error:', error);
            showToast('error', 'Error generating PDF');
        } finally {
            setPdfLoading(false);
        }
    };

    const printReport = () => {
        const printWindow = window.open('', '_blank');
        const totalIncoming = filteredTransactions.filter(t => isIncomingTransaction(t.transaction_type)).reduce((sum, t) => sum + parseFloat(t.amount), 0);
        const totalOutgoing = filteredTransactions.filter(t => isOutgoingTransaction(t.transaction_type)).reduce((sum, t) => sum + parseFloat(t.amount), 0);
        
        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Transactions Statement</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    h1 { color: #8b5cf6; }
                    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    th { background-color: #8b5cf6; color: white; }
                    .summary { margin: 20px 0; padding: 15px; background: #f3f4f6; border-radius: 8px; }
                    .summary-grid { display: flex; gap: 20px; flex-wrap: wrap; }
                    .summary-card { background: white; padding: 10px 20px; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
                    .footer { margin-top: 20px; text-align: center; font-size: 12px; color: #666; }
                </style>
            </head>
            <body>
                <h1>${translations.title || 'Transactions Flow Statement'}</h1>
                <p>${translations.generatedBy || 'Generated by'}: ${user?.username} | ${translations.reportDate || 'Date'}: ${new Date().toLocaleString()}</p>
                <p>${translations.shopName || 'Shop'}: ${currentShop?.shop_name}</p>
                
                <div class="summary">
                    <h3>Summary</h3>
                    <div class="summary-grid">
                        <div class="summary-card"><strong>Total Transactions:</strong> ${filteredTransactions.length}</div>
                        <div class="summary-card"><strong>Total Incoming:</strong> ${formatCurrencyWithSymbol(totalIncoming, 'USD')}</div>
                        <div class="summary-card"><strong>Total Outgoing:</strong> ${formatCurrencyWithSymbol(totalOutgoing, 'USD')}</div>
                        <div class="summary-card"><strong>Net Balance:</strong> ${formatCurrencyWithSymbol(totalIncoming - totalOutgoing, 'USD')}</div>
                    </div>
                </div>
                
                <table>
                    <thead>
                        <tr><th>Transaction No</th><th>Date</th><th>Type</th><th>Entity</th><th>Name</th><th>Amount</th><th>Currency</th></tr>
                    </thead>
                    <tbody>
                        ${filteredTransactions.slice(0, 200).map(t => `
                            <tr>
                                <td>${t.transaction_no}</td>
                                <td>${formatDate(t.transaction_date)}</td>
                                <td>${translations[t.transaction_type] || t.transaction_type}</td>
                                <td>${translations[t.entity_type] || t.entity_type}</td>
                                <td>${t.entity_name || '-'}</td>
                                <td>${formatCurrencyWithSymbol(parseFloat(t.amount), t.currency_code)}</td>
                                <td>${t.currency_code}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                <div class="footer"><p>Generated on ${new Date().toLocaleString()}</p></div>
            </body>
            </html>
        `;
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.print();
    };

    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentTransactions = filteredTransactions.slice(startIndex, startIndex + itemsPerPage);
    const pageCurrencyTotals = calculatePageCurrencyTotals(currentTransactions);

    const getEntityOptions = () => {
        switch (filterEntityType) {
            case 'customer': return customers;
            case 'supplier': return suppliers;
            case 'partner': return partners;
            case 'employee': return employees;
            case 'other': return others;
            default: return [];
        }
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faMoneyBillTransfer} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.title || 'Transactiosn Statment'}</h1>
                        
                       
                    </div>
                </div>

                {!currentShop ? (
                    <div className="shop-indicator-modern no-shop">
                        <div className="shop-badge">
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            <span className="shop-warning">{translations.noShopSelected || 'No shop selected'}</span>
                        </div>
                        <button className="btn-modern btn-switch-shop" onClick={() => setShowShopSelector(true)}>
                            <FontAwesomeIcon icon={faStore} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                ) : null}
            </div>

            {/* Summary Stats Section */}
            {/* {currentShop && summary && filteredTransactions.length > 0 && (
                <div className="summary-stats-modern">
                    <div className="stat-card">
                        <div className="stat-icon" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                            <FontAwesomeIcon icon={faChartBar} style={{ color: '#3b82f6' }} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{summary.totalCount}</div>
                            <div className="stat-label">{translations.totalTransactions || 'Total Transactions'}</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
                            <FontAwesomeIcon icon={faArrowDown} style={{ color: '#10b981' }} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value" style={{ color: '#10b981' }}>{formatCurrencyWithSymbol(summary.totalIncoming, 'USD')}</div>
                            <div className="stat-label">{translations.totalIncoming || 'Total Incoming'}</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                            <FontAwesomeIcon icon={faArrowUp} style={{ color: '#ef4444' }} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value" style={{ color: '#ef4444' }}>{formatCurrencyWithSymbol(summary.totalOutgoing, 'USD')}</div>
                            <div className="stat-label">{translations.totalOutgoing || 'Total Outgoing'}</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
                            <FontAwesomeIcon icon={faBalanceScale} style={{ color: '#8b5cf6' }} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{formatCurrencyWithSymbol(summary.netBalance, 'USD')}</div>
                            <div className="stat-label">{translations.netBalance || 'Net Balance'}</div>
                        </div>
                    </div>
                </div>
            )} */}

            {/* Currency Totals Panel */}
            {/* {currentShop && filteredTransactions.length > 0 && Object.keys(currencyTotals).length > 0 && (
                <div className="currency-totals-panel" style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px',
                    color: 'white'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <FontAwesomeIcon icon={faCoins} />
                            {translations.currencyTotals || 'Currency-wise Summary'}
                        </h4>
                        <button onClick={() => setShowCurrencyBreakdown(true)} style={{
                            background: 'rgba(255,255,255,0.2)',
                            border: 'none',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px'
                        }}>
                            <FontAwesomeIcon icon={faChartBar} /> View Detailed Breakdown
                        </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
                        {Object.entries(currencyTotals).map(([currency, data]) => (
                            <div key={currency} style={{
                                background: 'rgba(255,255,255,0.15)',
                                borderRadius: '10px',
                                padding: '15px',
                                backdropFilter: 'blur(10px)'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.2)', paddingBottom: '8px' }}>
                                    <strong style={{ fontSize: '20px' }}>{getCurrencySymbol(currency)} {currency}</strong>
                                    <span style={{ fontSize: '12px', opacity: 0.8 }}>{data.count} {translations.transactions || 'txns'}</span>
                                </div>
                                <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: '#a7f3d0' }}><FontAwesomeIcon icon={faArrowDown} /> {translations.incoming || 'Incoming'}:</span>
                                        <strong>{formatCurrencyWithSymbol(data.incoming, currency)}</strong>
                                    </div>
                                </div>
                                <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: '#fecaca' }}><FontAwesomeIcon icon={faArrowUp} /> {translations.outgoing || 'Outgoing'}:</span>
                                        <strong>{formatCurrencyWithSymbol(data.outgoing, currency)}</strong>
                                    </div>
                                </div>
                                <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '10px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.2)', display: 'flex', justifyContent: 'space-between', color: data.net >= 0 ? '#a7f3d0' : '#fecaca' }}>
                                    <span>{translations.netBalance || 'Net Balance'}:</span>
                                    <strong>{formatCurrencyWithSymbol(data.net, currency)}</strong>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )} */}

            {/* Filters Toggle */}
            <div className="filters-toggle" style={{ marginBottom: '15px' }}>
                <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} className="btn-modern btn-secondary-modern" style={{ padding: '8px 16px', fontSize: '13px' }}>
                    <FontAwesomeIcon icon={showAdvancedFilters ? faChevronUp : faChevronDown} />
                    <span>{showAdvancedFilters ? 'Hide Advanced Filters' : 'Show Advanced Filters'}</span>
                </button>
            </div>

            {/* Advanced Filters Section */}
            {showAdvancedFilters && (
                <div className="advanced-filters-section">
                    <div className="filters-row">
                        <div className="filter-group compact">
                            <select value={filterEntityType} onChange={(e) => { setFilterEntityType(e.target.value); setFilterEntityId(''); }} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>
                                <option value="">{translations.allEntities || 'All Entities'}</option>
                                {entityTypes.map(type => (<option key={type.value} value={type.value}>{translations[type.value] || type.label}</option>))}
                            </select>
                        </div>

                        {filterEntityType && getEntityOptions().length > 0 && (
                            <div className="filter-group compact">
                                <select value={filterEntityId} onChange={(e) => setFilterEntityId(e.target.value)} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>
                                    <option value="">All {translations[filterEntityType] || filterEntityType}s</option>
                                    {getEntityOptions().map(entity => (<option key={entity.id || entity.customerid || entity.supplierid || entity.partner_id || entity.employeeid || entity.other_id} value={entity.id || entity.customerid || entity.supplierid || entity.partner_id || entity.employeeid || entity.other_id}>{entity.name || entity.customer_name || entity.supplier_name || entity.partner_name || entity.employee_name || entity.other_name}</option>))}
                                </select>
                            </div>
                        )}

                        <div className="filter-group compact">
                            <select value={filterTransactionType} onChange={(e) => setFilterTransactionType(e.target.value)} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>
                                <option value="">{translations.allTypes || 'All Types'}</option>
                                {transactionTypes.map(type => (<option key={type.value} value={type.value}>{translations[type.value] || type.label}</option>))}
                            </select>
                        </div>

                        <div className="filter-group compact">
                            <select value={filterDirection} onChange={(e) => setFilterDirection(e.target.value)} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>
                                <option value="">{translations.allDirections || 'All Directions'}</option>
                                <option value="incoming">{translations.incoming || 'Incoming'}</option>
                                <option value="outgoing">{translations.outgoing || 'Outgoing'}</option>
                            </select>
                        </div>

                        <div className="filter-group compact">
                            <select value={filterPaymentMethod} onChange={(e) => setFilterPaymentMethod(e.target.value)} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>
                                <option value="">{translations.allMethods || 'All Methods'}</option>
                                {paymentMethods.map(method => (<option key={method.value} value={method.value}>{translations[method.value] || method.label}</option>))}
                            </select>
                        </div>

                        <div className="filter-group compact">
                            <select value={filterPaymentSource} onChange={(e) => setFilterPaymentSource(e.target.value)} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>
                                <option value="">{translations.sourceFilter || 'Payment Source'}</option>
                                <option value="owner">Owner</option>
                                <option value="partner">Partner</option>
                            </select>
                        </div>

                        <div className="filter-group compact">
                            <select value={filterCurrency} onChange={(e) => setFilterCurrency(e.target.value)} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>
                                <option value="">{translations.allCurrencies || 'All Currencies'}</option>
                                {currencies.map(currency => (<option key={currency} value={currency}>{currency}</option>))}
                            </select>
                        </div>

                        <div className="filter-group compact">
                            <select value={filterExpenseCategory} onChange={(e) => setFilterExpenseCategory(e.target.value)} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>
                                <option value="">{translations.allCategories || 'All Categories'}</option>
                                {expenseCategoryOptions.map(cat => (<option key={cat.value} value={cat.value}>{translations[cat.value] || cat.label}</option>))}
                            </select>
                        </div>
                    </div>

                    <div className="filters-row">
                        <div className="filter-group compact date-filter">
                            <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} max={getTodayDate()} className="filter-input compact date-input" placeholder={translations.startDate || 'Start Date'} />
                        </div>
                        <div className="filter-group compact date-filter">
                            <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} min={filterDateFrom} max={getTodayDate()} className="filter-input compact date-input" placeholder={translations.endDate || 'End Date'} />
                        </div>

                        <div className="filter-group compact">
                            <input type="number" value={filterAmountMin} onChange={(e) => setFilterAmountMin(e.target.value)} placeholder={translations.minAmount || 'Min Amount'} className="filter-input compact" step="0.01" min="0" />
                        </div>
                        <div className="filter-group compact">
                            <input type="number" value={filterAmountMax} onChange={(e) => setFilterAmountMax(e.target.value)} placeholder={translations.maxAmount || 'Max Amount'} className="filter-input compact" step="0.01" min="0" />
                        </div>

                        <div className="filter-group compact search-group">
                            <div className="search-box compact">
                                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                <input type="text" placeholder={translations.searchPlaceholder || 'Search...'} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && applyFilters()} dir={isRTL ? 'rtl' : 'ltr'} />
                            </div>
                        </div>

                        <div className="filter-group compact">
                            <button onClick={applyFilters} className="refresh-btn-modern compact" style={{ background: 'var(--primary-gradient, linear-gradient(135deg, #8b5cf6, #7c3aed))' }}>
                                <FontAwesomeIcon icon={faFilter} />
                                <span className="btn-text">{translations.applyFilters || 'Apply'}</span>
                            </button>
                        </div>

                        <div className="filter-group compact">
                            <button onClick={resetFilters} className="refresh-btn-modern compact" style={{ background: '#6b7280' }}>
                                <FontAwesomeIcon icon={faTimes} />
                                <span className="btn-text">{translations.resetFilters || 'Reset'}</span>
                            </button>
                        </div>

                        <div className="filter-group compact">
                            <button onClick={exportToCSV} className="refresh-btn-modern compact" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                                <FontAwesomeIcon icon={faDownload} />
                                <span className="btn-text">CSV</span>
                            </button>
                        </div>

                        <div className="filter-group compact">
                            <button onClick={exportToPDF} className="refresh-btn-modern compact" disabled={pdfLoading || filteredTransactions.length === 0} style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}>
                                {pdfLoading ? <div className="btn-spinner-small"></div> : <FontAwesomeIcon icon={faFilePdf} />}
                                <span className="btn-text">{translations.exportToPDF || 'PDF'}</span>
                            </button>
                        </div>

                        <div className="filter-group compact">
                            <button onClick={printReport} className="refresh-btn-modern compact" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                                <FontAwesomeIcon icon={faPrint} />
                                <span className="btn-text">{translations.print || 'Print'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner-modern"></div>
                    <p>{translations.loadingData || 'Loading transaction data...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="no-shop-container">
                    <div className="no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>Please select a shop to view transactions</p>
                        <button className="btn-modern btn-primary-modern" onClick={() => setShowShopSelector(true)}>
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : filteredTransactions.length === 0 ? (
                <div className="no-data-container">
                    <div className="no-data-message">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <h3>{translations.noTransactionsFound || 'No Transactions Found'}</h3>
                        <p>Try adjusting your filters or reset them to see all transactions</p>
                        <button onClick={resetFilters} className="btn-modern btn-secondary-modern">
                            <FontAwesomeIcon icon={faTimes} />
                            Reset Filters
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* Page Summary */}
                    <div className="page-summary" style={{
                        padding: '10px 20px',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '8px',
                        marginBottom: '15px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '10px',
                        fontSize: '13px'
                    }}>
                        <div>
                            <strong>Page {currentPage} of {totalPages}</strong> | 
                            Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredTransactions.length)} of {filteredTransactions.length} transactions
                        </div>
                        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                            {Object.entries(pageCurrencyTotals).map(([currency, data]) => (
                                <span key={currency}>
                                    {currency}: <span style={{ color: '#10b981' }}>+{formatCurrencyWithSymbol(data.incoming, currency)}</span> | 
                                    <span style={{ color: '#ef4444', marginLeft: '5px' }}>-{formatCurrencyWithSymbol(data.outgoing, currency)}</span>
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Table Section */}
                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="centered-cell">{translations.transactionDate || 'Date'}</th>
                                            <th className="centered-cell">{translations.transactionType || 'Type'}</th>
                                            <th className="centered-cell">{translations.entityType || 'Entity'}</th>
                                            <th className="centered-cell">{translations.entityName || 'Name'}</th>
                                            <th className="centered-cell">{translations.description || 'Description'}</th>
                                            <th className="centered-cell">{translations.amount || 'Amount'}</th>
                                            <th className="centered-cell">{translations.currency || 'Currency'}</th>
                                            <th className="centered-cell">{translations.paymentMethod || 'Method'}</th>
                                            <th className="centered-cell">{translations.referenceNumber || 'Reference'}</th>
                                            <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentTransactions.map((tx, idx) => (
                                            <tr key={tx.id}>
                                                <td className="id-cell centered-cell">{startIndex + idx + 1}</td>
                                                <td className="centered-cell">{formatDate(tx.transaction_date)}</td>
                                                <td className="centered-cell"><TypeBadge type={tx.transaction_type} translations={translations} /></td>
                                                <td className="centered-cell"><EntityBadge entityType={tx.entity_type} translations={translations} /></td>
                                                <td className="centered-cell"><span className="cell-text">{tx.entity_name || '-'}</span></td>
                                                <td className="centered-cell"><span className="cell-text description-text" style={{ fontSize: '12px', maxWidth: '150px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tx.description || '-'}</span></td>
                                                <td className="centered-cell">
                                                    <span className={`amount-text ${isIncomingTransaction(tx.transaction_type) ? 'amount-positive' : isOutgoingTransaction(tx.transaction_type) ? 'amount-negative' : ''}`}>
                                                        {isIncomingTransaction(tx.transaction_type) ? '+' : isOutgoingTransaction(tx.transaction_type) ? '-' : ''} 
                                                        {formatCurrencyWithSymbol(parseFloat(tx.amount), tx.currency_code)}
                                                    </span>
                                                </td>
                                                <td className="centered-cell"><span className="cell-text">{tx.currency_code}</span></td>
                                                <td className="centered-cell">
                                                    {tx.payment_method && <PaymentMethodBadge method={tx.payment_method} translations={translations} />}
                                                    {!tx.payment_method && <span className="cell-text">-</span>}
                                                </td>
                                                <td className="centered-cell"><span className="cell-text receipt-number">{tx.reference_number || tx.transaction_no || '-'}</span></td>
                                                <td className="actions-cell-modern centered-cell">
                                                    <div className="actions-content">
                                                        <button onClick={() => handleViewTransaction(tx)} className="action-btn-modern view-btn-modern" title={translations.viewDetails || 'View Details'}>
                                                            <FontAwesomeIcon icon={faEye} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    {filteredTransactions.length > itemsPerPage && (
                        <div className="pagination-modern">
                            <div className="pagination-info">
                                <span>{translations.showing || 'Showing'} {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredTransactions.length)} {translations.of || 'of'} {filteredTransactions.length} {translations.transactions || 'transactions'}</span>
                                <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(parseInt(e.target.value)); setCurrentPage(1); }} style={{ marginLeft: '15px', padding: '4px 8px', borderRadius: '6px', border: '1px solid #ddd' }}>
                                    <option value={20}>20 / page</option>
                                    <option value={50}>50 / page</option>
                                    <option value={100}>100 / page</option>
                                    <option value={200}>200 / page</option>
                                </select>
                            </div>
                            <div className="pagination-controls">
                                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="pagination-btn">
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>
                                <div className="page-numbers">
                                    {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 7) pageNum = i + 1;
                                        else if (currentPage <= 4) pageNum = i + 1;
                                        else if (currentPage >= totalPages - 3) pageNum = totalPages - 6 + i;
                                        else pageNum = currentPage - 3 + i;
                                        return (<button key={pageNum} onClick={() => setCurrentPage(pageNum)} className={`page-number ${currentPage === pageNum ? 'active' : ''}`}>{pageNum}</button>);
                                    })}
                                </div>
                                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="pagination-btn">
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Currency Breakdown Modal */}
            <CurrencyBreakdownModal
                isOpen={showCurrencyBreakdown}
                onClose={() => setShowCurrencyBreakdown(false)}
                transactions={filteredTransactions}
                translations={translations}
            />

            {/* View Transaction Modal */}
            {showViewModal && selectedTransaction && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content-modern view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div className="modal-icon"><FontAwesomeIcon icon={faMoneyBillTransfer} /></div>
                            <div className="modal-title"><h3>{translations.transactionDetails || 'Transaction Details'}</h3><span className="transaction-id-badge">#{selectedTransaction.transaction_no || selectedTransaction.id}</span></div>
                            <button className="close-btn-modern" onClick={() => setShowViewModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
                        </div>
                        <div className="modal-body-modern view-modal-body">
                            <div className="view-section">
                                <h4 className="view-section-title"><FontAwesomeIcon icon={faInfoCircle} /> General Information</h4>
                                <div className="view-grid two-columns">
                                    <div className="view-row"><span className="view-label">Transaction No:</span><span className="view-value">{selectedTransaction.transaction_no}</span></div>
                                    <div className="view-row"><span className="view-label">Date:</span><span className="view-value">{formatDate(selectedTransaction.transaction_date)}</span></div>
                                    <div className="view-row"><span className="view-label">Type:</span><span className="view-value"><TypeBadge type={selectedTransaction.transaction_type} translations={translations} size="md" /></span></div>
                                    <div className="view-row"><span className="view-label">Status:</span><span className="view-value">{selectedTransaction.status}</span></div>
                                    <div className="view-row"><span className="view-label">Entity Type:</span><span className="view-value"><EntityBadge entityType={selectedTransaction.entity_type} translations={translations} /></span></div>
                                    <div className="view-row"><span className="view-label">Entity Name:</span><span className="view-value">{selectedTransaction.entity_name || '-'}</span></div>
                                </div>
                            </div>
                            <div className="view-section">
                                <h4 className="view-section-title"><FontAwesomeIcon icon={faMoneyBill} /> Financial Details</h4>
                                <div className="view-grid two-columns">
                                    <div className="view-row"><span className="view-label">Amount:</span><span className="view-value total-amount" style={{ color: isIncomingTransaction(selectedTransaction.transaction_type) ? '#10b981' : isOutgoingTransaction(selectedTransaction.transaction_type) ? '#ef4444' : '#6b7280' }}>
                                        {isIncomingTransaction(selectedTransaction.transaction_type) ? '+ ' : isOutgoingTransaction(selectedTransaction.transaction_type) ? '- ' : ''}
                                        {formatCurrencyWithSymbol(parseFloat(selectedTransaction.amount), selectedTransaction.currency_code)}
                                    </span></div>
                                    <div className="view-row"><span className="view-label">Currency:</span><span className="view-value">{selectedTransaction.currency_code}</span></div>
                                    <div className="view-row"><span className="view-label">Exchange Rate:</span><span className="view-value">{selectedTransaction.exchange_rate || '1.00'}</span></div>
                                    <div className="view-row"><span className="view-label">Payment Method:</span><span className="view-value">{selectedTransaction.payment_method && <PaymentMethodBadge method={selectedTransaction.payment_method} translations={translations} />}</span></div>
                                    <div className="view-row"><span className="view-label">Payment Source:</span><span className="view-value">{selectedTransaction.payment_source}</span></div>
                                    {selectedTransaction.partner_name && (<div className="view-row"><span className="view-label">Partner:</span><span className="view-value">{selectedTransaction.partner_name}</span></div>)}
                                </div>
                            </div>
                            <div className="view-section">
                                <h4 className="view-section-title"><FontAwesomeIcon icon={faBalanceScale} /> Balance Information</h4>
                                <div className="view-grid two-columns">
                                    {selectedTransaction.entity_balance_before !== null && (<><div className="view-row"><span className="view-label">Balance Before:</span><span className="view-value">{formatCurrencyWithSymbol(parseFloat(selectedTransaction.entity_balance_before), selectedTransaction.currency_code)}</span></div><div className="view-row"><span className="view-label">Balance After:</span><span className="view-value">{formatCurrencyWithSymbol(parseFloat(selectedTransaction.entity_balance_after), selectedTransaction.currency_code)}</span></div></>)}
                                    {selectedTransaction.owner_balance_before !== null && (<><div className="view-row"><span className="view-label">Owner Balance Before:</span><span className="view-value">{formatCurrencyWithSymbol(parseFloat(selectedTransaction.owner_balance_before), 'USDT')}</span></div><div className="view-row"><span className="view-label">Owner Balance After:</span><span className="view-value">{formatCurrencyWithSymbol(parseFloat(selectedTransaction.owner_balance_after), 'USDT')}</span></div></>)}
                                </div>
                            </div>
                            <div className="view-section">
                                <h4 className="view-section-title"><FontAwesomeIcon icon={faFileInvoice} /> Reference Information</h4>
                                <div className="view-grid two-columns">
                                    <div className="view-row"><span className="view-label">Reference Number:</span><span className="view-value">{selectedTransaction.reference_number || '-'}</span></div>
                                    <div className="view-row"><span className="view-label">Reference Type:</span><span className="view-value">{selectedTransaction.reference_type || '-'}</span></div>
                                    {selectedTransaction.original_sale_id && (<div className="view-row"><span className="view-label">Original Sale ID:</span><span className="view-value">{selectedTransaction.original_sale_id}</span></div>)}
                                    {selectedTransaction.original_bill_number && (<div className="view-row"><span className="view-label">Original Bill:</span><span className="view-value">{selectedTransaction.original_bill_number}</span></div>)}
                                    {selectedTransaction.cheque_id && (<div className="view-row"><span className="view-label">Cheque ID:</span><span className="view-value">{selectedTransaction.cheque_id}</span></div>)}
                                </div>
                            </div>
                            {selectedTransaction.description && (<div className="view-section"><h4 className="view-section-title"><FontAwesomeIcon icon={faComment} /> Description</h4><div className="remarks-box">{selectedTransaction.description}</div></div>)}
                            {selectedTransaction.notes && (<div className="view-section"><h4 className="view-section-title"><FontAwesomeIcon icon={faStickyNote} /> Notes</h4><div className="remarks-box">{selectedTransaction.notes}</div></div>)}
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-modern btn-secondary-modern" onClick={() => setShowViewModal(false)}>{translations.close || 'Close'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FabsTransactionsStatement;